/* eslint-disable max-lines-per-function */
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Divider,
  Grid,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { gql, useMutation, useQuery } from "@apollo/client";
import { createTeamUser, createTeamUserVariables, PropertyContractSigningStatus, UserRole } from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import { useSendEnrollmentEmail } from "pages/login/actions";
import { CREATE_USER_MUTATION } from "pages/dashboard/settings/pages/team/create";
import {
  cleanUserFormOutput,
  type UserFormData,
} from "pages/dashboard/settings/pages/team/shared-create-update";
import {
  mergePropertyContractUpdatedIntoList,
  PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
} from "data/graphql/property-contract-updated-subscription";
import { usePropertyContractUpdatedSocket } from "./use-property-contract-updated-socket";

const PROPERTY_MANAGER_ROLE = UserRole.PROPERTY_MANAGER;

const PROPERTY_CONTRACT_SIGNING_STATUS_QUERY = gql`
  query PropertyContractSigningStatus {
    propertyContracts {
      id
      status
      pdfUrl
    }
  }
`;

export type ContractSuccessData = {
  variant: "pm" | "supervisor";
  signerEmail?: string;
  contractId?: string | null;
  propertyName?: string;
  orgId?: string;
  pdfUrl?: string | null;
};

const inviteTeamMemberSchema = yup.object({
  email: yup
    .string()
    .trim()
    .required("Email is required")
    .email("Invalid email"),
});

type InviteFormValues = yup.InferType<typeof inviteTeamMemberSchema>;

const SuccessInviteForm: React.FC<{
  onInvite: (email: string) => Promise<boolean>;
}> = ({ onInvite }) => (
  <Formik<InviteFormValues>
    initialValues={{ email: "" }}
    validationSchema={inviteTeamMemberSchema}
    validateOnMount
    onSubmit={async ({ email }, { resetForm }) => {
      const ok = await onInvite(email.trim().toLowerCase());
      if (ok) resetForm();
    }}
  >
    {({ values, errors, touched, handleChange, handleBlur, isSubmitting, isValid }) => (
      <Form>
        <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 1, mb: 2 }}>
          <Grid size={{ xs: 12, sm: 5, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              name="email"
              label="Email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.email && errors.email)}
              {...(touched.email && errors.email ? { helperText: errors.email } : {})}
              placeholder="team@example.com"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Role"
              value={PROPERTY_MANAGER_ROLE}
            >
              <MenuItem value={PROPERTY_MANAGER_ROLE}>Property Manager</MenuItem>
            </TextField>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 2 }}
            sx={{
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || !isValid}
            >
              Invite
            </Button>
          </Grid>
        </Grid>
      </Form>
    )}
  </Formik>
);

type Props = {
  data: ContractSuccessData;
};

const StepSuccess: React.FC<Props> = ({ data }) => {
  const watchContractStatus =
    data.variant === "supervisor" && Boolean(data.contractId);

  const { data: contractsPollData, subscribeToMore } = useQuery<PropertyContractSigningStatus>(
    PROPERTY_CONTRACT_SIGNING_STATUS_QUERY,
    {
      skip: !watchContractStatus,
      fetchPolicy: "cache-and-network",
    },
  );

  React.useEffect(() => {
    if (!watchContractStatus || !contractsPollData?.propertyContracts) {
      return () => {};
    }
    const unsubscribe = subscribeToMore({
      document: PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) =>
        mergePropertyContractUpdatedIntoList(prev, subscriptionData),
    });
    return () => {
      unsubscribe();
    };
  }, [watchContractStatus, contractsPollData?.propertyContracts, subscribeToMore]);
  const { contractId } = data;
  let contractRow: PropertyContractSigningStatus["propertyContracts"][number] | null = null;
  if (contractId) {
    contractRow =
      contractsPollData?.propertyContracts?.find((c) => c.id === contractId) ?? null;
  }
  const signedFromQuery = contractRow?.status === "signed";
  const signedFromSocket = usePropertyContractUpdatedSocket(
    data.orgId,
    contractId ?? null,
    watchContractStatus,
  );
  const contractSignedForDownload = signedFromQuery || signedFromSocket;
  const contractPdfUrl = contractRow?.pdfUrl ?? data.pdfUrl ?? null;

  const { openSnackbar } = useSnackbar();
  const sendEnrollmentEmail = useSendEnrollmentEmail();
  const [createUser] = useMutation<createTeamUser, createTeamUserVariables>(
    CREATE_USER_MUTATION,
    { refetchQueries: ["getTeamByOrgId"] },
  );

  const handleInvite = React.useCallback(
    async (email: string): Promise<boolean> => {
      const normalized = (email || "").trim().toLowerCase();
      if (!normalized || !data.orgId) return false;
      const userData: UserFormData = {
        firstName: "Pending",
        lastName: "User",
        email: normalized,
        roles: PROPERTY_MANAGER_ROLE,
        lists: [],
        organizationId: data.orgId,
        directPhone: null,
        title: null,
      };
      const variables = { data: cleanUserFormOutput(userData) };
      try {
        const resp = await createUser({ variables });
        const createdId = resp.data?.createUser?.id;
        if (createdId) {
          await sendEnrollmentEmail(createdId, openSnackbar);
          return true;
        }
        openSnackbar({ message: "Failed to invite", status: "error" });
        return false;
      } catch {
        openSnackbar({ message: "Failed to invite", status: "error" });
        return false;
      }
    },
    [data.orgId, createUser, sendEnrollmentEmail, openSnackbar],
  );

  if (data.variant === "pm") {
    return (
      <>
        <Typography variant="h6">Contract sent!</Typography>
        <Divider style={{ width: "100%", margin: "8px 0" }} />
        <Typography variant="body1" gutterBottom>
          You&apos;ll get an email when {data.signerEmail} signs so you can configure your waitlist.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to="/dash/manager/lists"
          sx={{ mt: 2 }}
        >
          Go to Dashboard
        </Button>
      </>
    );
  }

  return (
    <>
      <Typography variant="h6">Contract signed!</Typography>
      <Divider style={{ width: "100%", margin: "8px 0" }} />
      <Typography variant="body1" gutterBottom>
        Add team members to configure your waitlist.
      </Typography>
      {data.orgId && (
        <SuccessInviteForm onInvite={handleInvite} />
      )}
      {data.contractId && contractSignedForDownload && contractPdfUrl && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          <Link
            href={contractPdfUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <DownloadIcon fontSize="small" />
            Download contract
          </Link>
        </Typography>
      )}
    </>
  );
};

export default StepSuccess;
